import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { AUTH_STORAGE_KEY, lerCorpoDoToken, lerTokenDeAcesso } from "@/lib/authToken";

export interface Tenant {
  id: string;
  slug: string;
  name: string;
  cnpj?: string;
  status: "active" | "inactive";
}

export interface TenantContextType {
  currentTenant: Tenant | null;
  setCurrentTenant: (tenant: Tenant | null) => void;
  isLoading: boolean;
  availableTenants: Tenant[];
  switchTenant: (tenantId: string) => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);

  useEffect(() => {
    // Load tenant from localStorage or JWT
    const loadTenant = async () => {
      try {
        // 🔴 Lia `localStorage["access_token"]`, uma chave LEGADA que o login
        //    não escreve — só o `switchTenant` daqui de baixo gravava, e nada
        //    limpava. Resultado em produção, 31/08/2026: ou o token era de uma
        //    sessão antiga (e `/auth/tenants` devolvia 401 no console), ou não
        //    existia — e aí `currentTenant` nunca era definido e o
        //    `useTTSAlert` desistia em silêncio, porque começa com
        //    `if (!currentTenant?.id) return`. Ver `@/lib/authToken`.
        const token = lerTokenDeAcesso();
        const payload = lerCorpoDoToken<{
          company_id?: string;
          company_slug?: string;
          company_name?: string;
        }>();
        if (token && payload) {
          if (payload.company_id && payload.company_slug) {
            const tenant: Tenant = {
              id: payload.company_id,
              slug: payload.company_slug,
              name: payload.company_name || "Default Company",
              status: "active",
            };
            setCurrentTenant(tenant);

            // Load available tenants for user
            const response = await fetch("/api/v1/auth/tenants", {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
            if (response.ok) {
              const tenants = await response.json();
              setAvailableTenants(tenants);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load tenant:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTenant();
  }, []);

  const switchTenant = async (tenantId: string) => {
    try {
      setIsLoading(true);
      const token = lerTokenDeAcesso();

      if (token) {
        // Call API to switch tenant
        const response = await fetch("/api/v1/auth/switch-tenant", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ company_id: tenantId }),
        });

        if (response.ok) {
          const data = await response.json();

          // Update token with new tenant info
          if (data.access_token) {
            // ⚠️ Grava DENTRO de `seweb_auth`, preservando o resto da sessão.
            //    Gravar a chave solta `access_token` era o que criava o token
            //    órfão que ninguém limpava — a origem do 401 de partida.
            try {
              const bruto = localStorage.getItem(AUTH_STORAGE_KEY);
              const sessao = bruto ? JSON.parse(bruto) : {};
              localStorage.setItem(
                AUTH_STORAGE_KEY,
                JSON.stringify({ ...sessao, access_token: data.access_token })
              );
            } catch {
              /* localStorage bloqueado — a troca de tenant vale só nesta aba */
            }

            // Decode new token to get tenant info
            const payload = JSON.parse(atob(data.access_token.split(".")[1]));
            const tenant: Tenant = {
              id: payload.company_id,
              slug: payload.company_slug,
              name: payload.company_name || "Default Company",
              status: "active",
            };

            setCurrentTenant(tenant);

            // Reload page to apply new tenant context
            window.location.reload();
          }
        }
      }
    } catch (error) {
      console.error("Failed to switch tenant:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const value: TenantContextType = {
    currentTenant,
    setCurrentTenant,
    isLoading,
    availableTenants,
    switchTenant,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};

export default TenantContext;
